import React, { useCallback, useEffect, useRef } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import TimHortonsLogo from 'components/icons/tim-hortons-logo';
import { ToastContainer } from 'components/toast';
import useUserVerification from 'hooks/use-user-verification';
import { useAuthContext } from 'state/auth';
import { toast } from 'utils/toast';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${Styles.color.background};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Styles.color.primary};
  // Match the main app nav bar (height + red) so the microsite header is
  // consistent with the rest of the site.
  height: ${Styles.layout.navHeight.mobile};
  padding: 0 1rem;

  ${Styles.desktop`
    height: ${Styles.layout.navHeight.desktop};
  `}

  svg {
    height: 1.5rem;
    width: auto;

    // TH brand logo is red by default; invert to white over the red banner.
    path {
      fill: ${Styles.color.white};
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  // Top-aligned below the header, never centered (Figma Partnerships 1377:61836).
  justify-content: flex-start;
  text-align: center;
  padding: 2rem 1rem;
  margin: 0 auto;
  width: 100%;
  max-width: 24rem;

  ${Styles.desktop`
    max-width: ${Styles.layout.smallerSectionMaxWidth};
    padding: 6.5rem 1rem 3rem;
  `}
`;

const Title = styled.h1`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  line-height: 2rem;
  margin: 0.5rem 0;

  ${Styles.desktop`
    font-size: 2rem;
    line-height: 2.5rem;
  `}

  // Suppressed for the programmatic mount focus; kept for keyboard users.
  outline: none;

  &:focus-visible {
    outline: 2px solid ${Styles.color.tertiary};
    outline-offset: 2px;
  }
`;

const Description = styled.p`
  // Figma Tokens/secondary-1 — the design system token isn't wired into Styles.
  color: #654d4d;
  font-size: 1rem;
  line-height: 1.5rem;
  // 16px to the CTA, per the design's empty state.
  margin: 0 0 1rem 0;
`;

const CtaWrapper = styled.div`
  // Shown at every breakpoint, unlike the sibling block screens: their CTA is
  // only a way back, while this one is the guest's recovery action.
  display: flex;
  justify-content: center;
  width: 100%;

  // Figma's Primary Button Large (Partnerships 2306:4849) is a hug-width
  // rectangle, not the shared full-width pill. Scoped to the wrapper so the rest
  // of the app keeps the pill, and so it outranks the library class whatever the
  // injection order.
  button {
    width: auto;
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

interface IVerificationRequiredProps {
  onBackToApp: () => void;
  /**
   * The handoff token when one drove the flow, so the send authenticates as the
   * blocked account rather than whatever session this browser holds. `null` for
   * the ordinary web flow, which sends as the session.
   */
  idToken: string | null;
}

/**
 * Shown to a signed-in but email-unverified guest reaching CT linking
 * (THLE-4186). Copy and layout follow the web design (Partnerships node
 * 2304:3829 EN, 3189:5786 FR): heading, body, one Verify CTA.
 *
 * Verifying does not clear the gate on its own — the claim is read off the ID
 * token, so the guest needs a fresh one via re-authentication.
 */
const VerificationRequired: React.FC<IVerificationRequiredProps> = ({ onBackToApp, idToken }) => {
  const { formatMessage } = useIntl();
  const { currentUserSession } = useAuthContext();
  // Without `allowOtpHandoff: false`, `EXP_UNIFIED_AUTH_ENTRY_WEB` would sign the
  // guest out and leave for `confirmOtp`, abandoning the link in progress.
  const { sendVerificationEmail, loading } = useUserVerification({
    idToken,
    allowOtpHandoff: false,
  });
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Replaces the loading state in place, so there is no route change for
  // assistive tech to announce.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // The session rather than the profile, which resolves a beat later and would
  // swap the CTA out mid-paint.
  const canSendVerification = Boolean(idToken) || Boolean(currentUserSession);

  // A rejection gets its own message rather than the shared confirmation, since
  // this CTA is the guest's only way forward.
  const onVerify = useCallback(async () => {
    const sent = await sendVerificationEmail();
    if (sent) {
      toast.success(formatMessage({ id: 'emailVerificationSent' }));
      return;
    }
    toast.error(formatMessage({ id: 'ctLinkingVerificationRequiredSendError' }));
  }, [formatMessage, sendVerificationEmail]);

  return (
    <Page data-testid="ct-verification-required">
      <Helmet title={formatMessage({ id: 'ctLinkingVerificationRequiredTitle' })} />
      <Header>
        <TimHortonsLogo />
      </Header>
      <Content>
        <Title ref={headingRef} tabIndex={-1}>
          {formatMessage({ id: 'ctLinkingVerificationRequiredTitle' })}
        </Title>
        <Description>
          {formatMessage({ id: 'ctLinkingVerificationRequiredDescription' })}
        </Description>
        <CtaWrapper>
          {canSendVerification ? (
            <ActionButton isLoading={loading} onClick={onVerify}>
              {formatMessage({ id: 'ctLinkingVerificationRequiredVerifyCta' })}
            </ActionButton>
          ) : (
            <ActionButton onClick={onBackToApp}>
              {formatMessage({ id: 'ctLinkingVerificationRequiredBackToAppCta' })}
            </ActionButton>
          )}
        </CtaWrapper>
      </Content>
      {/* This microsite renders outside `Layout`, where the app mounts its only
          `ToastContainer`, so the send outcome would go unreported without one. */}
      <ToastContainer />
    </Page>
  );
};

export default VerificationRequired;
